/* eslint-disable react/no-danger */
import React from "react";
import PropTypes from "prop-types";
import TextExpander from "../../../../components/TextExpander";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import { checkIfDbNSFPGenesEmpty } from "./helpers";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import ZebraResult from "./ZebraResult";
import MouseResult from "./MouseResult";
import GoResult from "./GoResult";
import Pathways from "./Pathways";
import DiseaseDescriptions from "./DiseaseDescriptions";
import GeneDamagePredictionResult from "./GeneDamagePredictionResult";
import ExpressionData from "./ExpressionData";
import Expressions from "./Expressions";
import Table from "./Table";

const DbNSFPGenes = (props) => {
  const { data, gene } = props;
  if (
    !data ||
    (data && data.items && !Array.isArray(data.items) && !data.items[0])
  ) {
    return null;
  }

  const isEmpty = checkIfDbNSFPGenesEmpty(data.items[0]);

  if (!isEmpty) {
    const items = data.items.map((item, i) => {
      // If dbnsfp's gene symbol isn't the one we're looking at (possibly because of HGNC rename), don't display it.
      if (item.gene_symbol !== gene && data.items.length > 1) {
        return null;
      }

      return (
        <div key={i}>
          <Expressions item={item} idx={i} />
          {item.gene_damage_prediction && (
            <GeneDamagePredictionResult item={item} />
          )}
          {((item?.kegg && Object.keys(item?.kegg || {}).length > 0) ||
            (Array.isArray(item.pathway_biocarta) &&
              item.pathway_biocarta.length > 0) ||
            (Array.isArray(item.pathway_uniprot) &&
              item.pathway_uniprot.length > 0) ||
            (Array.isArray(item.pathway_consensuspathdb) &&
              item.pathway_consensuspathdb.length > 0)) && (
            <Pathways item={item} />
          )}
          {(item.p_rec || item.known_recessive_info) && (
            <Table
              item={item}
              prefix={["recessive"]}
              title="Recessive Scores"
            />
          )}
          {(item.loftool_score ||
            item.rvis_evs ||
            item.rvis_evs_percentile) && (
            <Table
              item={item}
              prefix={["intolerance"]}
              title="Intolerance Scores"
            />
          )}
          {(item.p_hi || item.hipred || item.hipred_score || item.ghis) && (
            <Table
              item={item}
              prefix={["haploinsufficiency"]}
              title="Haploinsufficiency Scores"
            />
          )}
          {(item.essential_gene ||
            item.essential_crispr ||
            item.essential_crispr2 ||
            item.essential_mutagenesis ||
            item.indispensability_score ||
            item.indispensability_pred) && (
            <Table
              item={item}
              prefix={["essentials", "indispensability"]}
              title="Essentials"
            />
          )}
          <DiseaseDescriptions descriptions={item?.disease_description} />
          {item.function_description && (
            <div className="tw-mt-4">
              <div>
                <h5> Function Description (UniProt)</h5>
                <TextExpander text={item.function_description} truncate="300" />
              </div>
            </div>
          )}
          {((Array.isArray(item.expression_gnf_atlas) &&
            item.expression_gnf_atlas.length > 0) ||
            (Array.isArray(item.expression_egenetics) &&
              item.expression_egenetics.length > 0)) && (
            <ExpressionData item={item} />
          )}
          <GoResult go={item?.go} gene={item?.gene_symbol} />
          <MouseResult
            mousePhenotype={item?.mgi_mouse_phenotype}
            mouseGene={item?.mgi_mouse_gene}
          />
          {(item?.zfin_zebrafish_gene ||
            item?.zfin_zebrafish_phenotype_quality ||
            item?.zfin_zebrafish_phenotype_tag ||
            item?.zfin_zebrafish_structure) && <ZebraResult item={item} />}
        </div>
      );
    });

    return (
      <div className="varsome-result-fixed" data-testid="dbnsfp-component">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            title="dbNSFP Genes"
            version={data?.version}
            url={getVarsoUrl("https://sites.google.com/site/jpopgen/dbNSFP")}
            content={<div className="varsome-inner-container">{items}</div>}
          />
        </div>
      </div>
    );
  }

  return null;
};

const { string, number, bool } = PropTypes;

DbNSFPGenes.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        disease_description: PropTypes.arrayOf(string),
        essential_crispr: string,
        essential_crispr2: string,
        essential_gene: string,
        essential_mutagenesis: string,
        expression_egenetics: PropTypes.arrayOf(string),
        expression_gnf_atlas: PropTypes.arrayOf(string),
        expressions_rpkm: PropTypes.shape({
          type: string,
          required: bool,
          read_only: bool,
          label: string,
        }),
        function_description: string,
        gdi: number,
        gdi_phred: number,
        gene_damage_prediction: PropTypes.shape({
          primary_immunodeficiency: PropTypes.shape({
            all: string,
            autosomaldominant: string,
            autosomalrecessive: string,
          }),
          mendelian: PropTypes.shape({
            all: string,
            autosomaldominant: string,
            autosomalrecessive: string,
          }),
          cancer: PropTypes.shape({
            all: string,
            autosomaldominant: string,
            autosomalrecessive: string,
          }),
        }),
        gene_symbol: string,
        ghis: number,
        go: PropTypes.shape({
          molecular_function: PropTypes.arrayOf(string),
          biological_process: PropTypes.arrayOf(string),
          cellular_component: PropTypes.arrayOf(string),
        }),
        hipred: bool,
        hipred_score: number,
        indispensability_pred: string,
        indispensability_score: number,
        kegg: PropTypes.shape({
          id: PropTypes.arrayOf(string),
          full: PropTypes.arrayOf(string),
        }),
        known_recessive_info: string,
        loftool_score: number,
        p_hi: number,
        p_rec: number,
        pathway_biocarta: PropTypes.arrayOf(string),
        pathway_consensuspathdb: PropTypes.arrayOf(string),
        pathway_uniprot: PropTypes.arrayOf(string),
        rvis_evs: number,
        rvis_evs_percentile: string,
      })
    ),
    version: PropTypes.oneOfType([string, PropTypes.number]),
  }),
  gene: string,
};

DbNSFPGenes.defaultProps = {
  data: null,
  gene: "",
};

export default withErrorGuard(DbNSFPGenes, "DbNSFPGenes", "dbNSFP Genes");
